// Browser-side port of evessio-sanitiser. Strips raw Evessio nomination
// JSON dumps down to the whitelisted fields the WAF admin portal actually
// consumes. No fs/path here — this runs against in-memory parsed JSON
// from a file upload.

import {
  NOMINATION_TOP_LEVEL,
  ALLOWED_QUESTION_FIELDS,
  TRANSACTION_FIELDS,
} from './allowedFields';

export interface SanitisedQuestion {
  field: string;
  value: string;
}

export interface SanitisedTransaction {
  transaction_status?: string;
}

export interface SanitisedEntry {
  uuid?: string;
  nomination_code?: string;
  category_name?: string;
  customer_firstname?: string;
  customer_lastname?: string;
  customer_email?: string;
  transaction?: SanitisedTransaction;
  questions: SanitisedQuestion[];
}

export interface SanitisedPayload {
  entries: SanitisedEntry[];
}

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function pick<T extends Record<string, unknown>>(
  obj: T,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  }
  return out;
}

function sanitiseQuestion(q: unknown): SanitisedQuestion | null {
  if (!isObject(q)) return null;
  const field = q.field;
  if (typeof field !== 'string') return null;
  if (!ALLOWED_QUESTION_FIELDS.includes(field)) return null;
  const value = q.value;
  return { field, value: value == null ? '' : String(value) };
}

function sanitiseEntry(nom: unknown): SanitisedEntry | null {
  if (!isObject(nom)) return null;
  const top = pick(nom, NOMINATION_TOP_LEVEL);

  // Whitelist whatever survives off the nested transaction object —
  // currently just `transaction_status`. If the nomination has no
  // transaction we omit the key entirely rather than emit `transaction: {}`.
  let transaction: SanitisedTransaction | undefined;
  if (isObject(nom.transaction)) {
    const picked = pick(nom.transaction, TRANSACTION_FIELDS);
    if (Object.keys(picked).length > 0) transaction = picked as SanitisedTransaction;
  }

  const rawQs: SanitisedQuestion[] = Array.isArray(nom.questions)
    ? (nom.questions
        .map(sanitiseQuestion)
        .filter((x): x is SanitisedQuestion => x !== null))
    : [];
  // Sort by canonical display order defined in allowedFields.ts so the
  // output is readable (hero -> Image 2 + credit -> Image 3 + credit -> ...).
  // Fields not found in the whitelist would have been dropped already, but
  // guard with a big fallback index just in case.
  const orderIdx = (f: string) => {
    const i = ALLOWED_QUESTION_FIELDS.indexOf(f);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  rawQs.sort((a, b) => orderIdx(a.field) - orderIdx(b.field));
  return {
    ...top,
    ...(transaction ? { transaction } : {}),
    questions: rawQs,
  } as SanitisedEntry;
}

// Evessio has shipped nominations under both `root.nominations` and as the
// root array. Accept either.
function extractEntries(parsed: unknown): unknown[] {
  if (Array.isArray(parsed)) return parsed;
  if (isObject(parsed) && Array.isArray(parsed.nominations)) {
    return parsed.nominations;
  }
  throw new Error('nominations JSON: expected an array or { nominations: [...] }');
}

export function sanitise(rawNominations: unknown): SanitisedPayload {
  const entries = extractEntries(rawNominations)
    .map(sanitiseEntry)
    .filter((x): x is SanitisedEntry => x !== null);
  return { entries };
}
